import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import { fetchProducts as fetchAllProducts } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { getWhatsAppUrl } from '../components/WhatsAppButton';
import { CATEGORIES } from '../lib/categories';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await fetchAllProducts();
      setProducts(data.slice(0, 8));
      setTrendingProducts(data.filter((p) => p.trending).slice(0, 4));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Show first 6 categories on the homepage; full list lives on /categories
  const categories = CATEGORIES.slice(0, 6);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden px-4"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/hero-banner.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30 z-10" />

        <div className="relative z-20 text-center w-full max-w-5xl mx-auto py-20">
          <motion.h1
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="logo-font text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-foreground mb-4 sm:mb-6"
            data-testid="hero-title"
          >
            POSTERLY
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-foreground/90 text-base sm:text-lg md:text-xl mb-6 sm:mb-8 font-medium px-4"
            data-testid="hero-tagline"
          >
            Make Your Walls Impossible to Ignore
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div
              animate={{
                boxShadow: [
                  "0 0 0 0 rgba(255, 255, 255, 0.7)",
                  "0 0 0 10px rgba(255, 255, 255, 0)",
                  "0 0 0 0 rgba(255, 255, 255, 0)"
                ]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1
              }}
              className="inline-block rounded-sm"
            >
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 bg-white text-black px-6 sm:px-8 py-3 sm:py-4 rounded-sm font-medium hover:bg-white/90 transition-all hover:-translate-y-1 hover:shadow-2xl text-sm sm:text-base"
                data-testid="shop-now-button"
              >
                Shop Now
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight size={20} weight="bold" />
                </motion.div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-testid="categories-section">
        <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight mb-12 text-center">
          Browse by Category
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {categories.map((category, index) => (
            <motion.div
              key={category.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Link to={`/shop?category=${encodeURIComponent(category.name)}`}>
                <div className="group relative aspect-square overflow-hidden bg-secondary border border-border/40 rounded-sm">
                  <img
                    src={category.image}
                    alt={category.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-2"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-500" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <h3 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight transition-all duration-500 group-hover:scale-110 group-hover:tracking-wide">
                      {category.name}
                    </h3>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 text-foreground/80 hover:text-foreground transition-colors text-sm sm:text-base font-medium border-b border-foreground/40 hover:border-foreground pb-1"
            data-testid="view-all-categories-link"
          >
            View all categories
            <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Featured Products */}
      {products.length > 0 && (
        <section className="py-20 px-4 max-w-7xl mx-auto" data-testid="featured-section">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight mb-12 text-center">
            Featured Posters
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 grid-flow-row-dense">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      {/* Trending Now */}
      {trendingProducts.length > 0 && (
        <section className="py-12 sm:py-16 md:py-20 px-4 max-w-7xl mx-auto" data-testid="trending-section">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground tracking-tight mb-8 sm:mb-12 text-center">
            Trending Now
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 grid-flow-row-dense">
            {trendingProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      {/* Custom Order CTA */}
      <section className="py-12 sm:py-16 md:py-20 px-4 max-w-4xl mx-auto text-center" data-testid="custom-order-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-secondary border border-border/40 rounded-sm p-6 sm:p-8 md:p-12"
        >
          <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl text-foreground tracking-tight mb-3 sm:mb-4">
            Want Something Custom?
          </h2>
          <p className="text-foreground/80 mb-6 sm:mb-8 text-sm sm:text-base md:text-lg px-2">
            We create personalized posters just for you. Share your vision with us!
          </p>
          <button
            onClick={() => {
              const url = getWhatsAppUrl((process.env.REACT_APP_WHATSAPP_NUMBER || '917416970039'), 'Hi, I want to order a custom poster!');
              window.open(url, '_blank');
            }}
            className="inline-flex items-center gap-2 bg-white text-black px-6 sm:px-8 py-3 sm:py-4 rounded-sm font-medium hover:bg-white/90 transition-all text-sm sm:text-base"
            data-testid="custom-order-button"
          >
            Request Custom Poster
            <ArrowRight size={20} weight="bold" />
          </button>
        </motion.div>
      </section>
    </div>
  );
};

export default Home;
